// Record-level edit operations (ADR 0009): `put` a whole record, `delete` one by key.
// Shared by the server (applies batches to disk) and the app (applies pending ops locally).

import { recordHash } from "./ids.ts";
import { recordLine } from "./serialize.ts";
import type { AnyRecord, Doc, Records, RecordType } from "./types.ts";

/** `base` is the hash of the record as the client last saw it (null: it didn't exist). */
export type PutOp<K extends RecordType = RecordType> = { op: "put"; type: K; base: string | null; record: Records[K] };
export type DeleteOp<K extends RecordType = RecordType> = { op: "delete"; type: K; base: string | null; key: string };
export type Op = PutOp | DeleteOp;

export const RECORD_TYPES: readonly RecordType[] = ["project", "pages", "departments", "noteLayers", "moments", "cues", "standbys", "notes"];

/** The key of a record: its id, its page number for page table entries, `project` for the project. */
export function recordKey(type: RecordType, record: AnyRecord): string {
  if (type === "project") return "project";
  if (type === "pages" && "page" in record) return String(record.page);
  return "id" in record ? record.id : "";
}

export function findRecord<K extends RecordType>(doc: Doc, type: K, key: string): Records[K] | undefined {
  if (type === "project") return doc.project as Records[K];
  const list = doc[type as Exclude<RecordType, "project">] as AnyRecord[];
  return list.find((r) => recordKey(type, r) === key) as Records[K] | undefined;
}

export interface Applied {
  op: Op;
  /** The record that was there before (undefined: none). */
  before: AnyRecord | undefined;
  /** True when the op's base didn't match: another edit was replaced (last write wins). */
  conflict: boolean;
  /** False when the op changed nothing (an idempotent retry). */
  changed: boolean;
}

/** Apply one op in place. Never throws for a stale base: the op wins and the result says so. */
export function applyOp(doc: Doc, op: Op): Applied {
  const key = op.op === "put" ? recordKey(op.type, op.record) : op.key;
  const before = findRecord(doc, op.type, key);
  const beforeHash = recordHash(op.type, before);
  if (op.op === "put") {
    if (before !== undefined && recordLine(op.type, before) === recordLine(op.type, op.record)) {
      return { op, before, conflict: false, changed: false };
    }
    const record = structuredClone(op.record);
    if (op.type === "project") doc.project = record as Records["project"];
    else {
      const list = doc[op.type as Exclude<RecordType, "project">] as AnyRecord[];
      const i = before === undefined ? -1 : list.indexOf(before);
      if (i >= 0) list[i] = record;
      else list.push(record);
    }
    return { op, before, conflict: op.base !== beforeHash, changed: true };
  }
  if (before === undefined || op.type === "project") return { op, before, conflict: false, changed: false };
  const list = doc[op.type as Exclude<RecordType, "project">] as AnyRecord[];
  list.splice(list.indexOf(before), 1);
  return { op, before, conflict: op.base !== beforeHash, changed: true };
}

/** Structural check of an incoming op (shape only; the schema validates the result). */
export function isOp(v: unknown): v is Op {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  if (!RECORD_TYPES.includes(o.type as RecordType)) return false;
  if (o.base !== null && typeof o.base !== "string") return false;
  if (o.op === "put") return !!o.record && typeof o.record === "object";
  if (o.op === "delete") return typeof o.key === "string";
  return false;
}
